/**
 * The chat panel — a docked drawer (same slot as Notes / Journal / Help /
 * News, `ui.chatOpen`) with two tabs: the community room (PublicRoom) and the
 * user's own support ticket (SupportWidget). Plus ChatRailButton, the right
 * rail's toggle with the support unread badge.
 *
 * Only the active tab is mounted, and nothing while the panel is closed. Both
 * tabs are built for that: their room holds live in chatSlice and outlast an
 * unmount, so switching back renders the cached thread with no rejoin or
 * refetch. It also keeps the read signal honest — SupportWidget marks its
 * ticket read while mounted (`useRoomViewing`), so a hidden-but-mounted
 * Support tab would clear the badge for messages nobody saw.
 *
 * Opening defaults to the tab last used (Public room at first), or Support
 * when the badge shows unread replies. Not Support by default: opening it
 * creates the user's support room (GET /chat/rooms/me) for anyone who merely
 * looks at chat. The inbox hides a room until it holds a message, so that
 * room stays out of the supporters' queue.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchMyChatUnread } from "@/state/slices/chatSlice";
import { setChatOpen, setChatTab, type ChatPanelTab } from "@/state/slices/uiSlice";
import { PublicRoom } from "./PublicRoom";
import { SupportWidget } from "./SupportWidget";
import styles from "./ChatPanel.module.css";

function HashIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </svg>
  );
}

function HeadsetIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path d="M12 3.5c-4.7 0-8.5 3.4-8.5 7.7 0 2.4 1.2 4.5 3.1 5.9v3.4l3.1-1.7c.7.2 1.5.3 2.3.3 4.7 0 8.5-3.4 8.5-7.9S16.7 3.5 12 3.5z" fill="#fff" />
      <circle cx="8.3" cy="11.3" r="1.25" fill="#7b5cff" />
      <circle cx="12" cy="11.3" r="1.25" fill="#7b5cff" />
      <circle cx="15.7" cy="11.3" r="1.25" fill="#7b5cff" />
    </svg>
  );
}

const TABS: { key: ChatPanelTab; label: string; Icon: () => JSX.Element }[] = [
  { key: "public", label: "Public room", Icon: HashIcon },
  { key: "support", label: "Support", Icon: HeadsetIcon },
];

export function ChatPanel() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.chatOpen);
  const tab = useAppSelector((s) => s.ui.chatTab);

  // Close on Escape, mirroring the other docked drawers.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setChatOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  if (!open) return null;

  return (
    <aside className={styles.panel} aria-label="Chat">
      <div className={styles.header}>
        <h2 className={styles.title}>Chat</h2>
        <button
          type="button"
          className={styles.close}
          aria-label="Close chat"
          onClick={() => dispatch(setChatOpen(false))}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className={styles.tabs} role="tablist" aria-label="Chat rooms">
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`chat-tab-${key}`}
            aria-selected={tab === key}
            aria-controls="chat-tabpanel"
            className={`${styles.tab} ${tab === key ? styles.tabOn : ""}`}
            onClick={() => dispatch(setChatTab(key))}
          >
            <Icon />
            {label}
          </button>
        ))}
      </div>
      <div
        className={styles.body}
        role="tabpanel"
        id="chat-tabpanel"
        aria-labelledby={`chat-tab-${tab}`}
      >
        {tab === "public" ? <PublicRoom /> : <SupportWidget />}
      </div>
    </aside>
  );
}

/** The support unread count as a badge; nothing at 0. */
export function ChatUnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return <span className={styles.badge}>{count > 9 ? "9+" : count}</span>;
}

/** How often the rail re-reads the badge. Live replies only arrive for a
 *  room this session has joined (the Support tab holds it once opened); the
 *  poll covers a reply to a ticket the user hasn't opened since loading. */
export const CHAT_UNREAD_POLL_MS = 20_000;

/** The right rail's chat toggle. Styled by the rail (`className`,
 *  `activeClassName`); the badge is the user's unread support replies, read
 *  on mount and every CHAT_UNREAD_POLL_MS from /chat/rooms/me/unread — which,
 *  unlike /chat/rooms/me, never creates the user's room. */
export function ChatRailButton({
  className,
  activeClassName,
}: {
  className: string;
  activeClassName: string;
}) {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.chatOpen);
  const unread = useAppSelector((s) => s.chat.myRoomUnread);

  useEffect(() => {
    const load = () => void dispatch(fetchMyChatUnread());
    load();
    const id = window.setInterval(load, CHAT_UNREAD_POLL_MS);
    return () => window.clearInterval(id);
  }, [dispatch]);

  const toggle = () => {
    if (!open && unread > 0) dispatch(setChatTab("support"));
    dispatch(setChatOpen(!open));
  };

  return (
    <button
      type="button"
      className={[
        className,
        styles.railButton,
        open ? activeClassName : "",
        unread > 0 ? styles.railUnread : "",
      ]
        .filter(Boolean)
        .join(" ")}
      title={unread > 0 ? `Chat (${unread} unread)` : "Chat"}
      aria-label={`Chat${unread > 0 ? ` (${unread} unread)` : ""}`}
      aria-pressed={open}
      onClick={toggle}
    >
      <ChatIcon />
      <ChatUnreadBadge count={unread} />
    </button>
  );
}
